// TypeScript-based theme system that programmatically applies CSS custom properties
// This ensures themes persist through build processes and align with El Toro's design system

export interface ChangelogTheme {
  // Base colors
  '--changelog-bg': string
  '--changelog-text': string
  '--changelog-text-subdued': string
  '--changelog-border': string
  '--changelog-filter-bg': string

  // El Toro brand colors
  '--changelog-primary': string
  '--changelog-primary-text': string
  '--changelog-secondary': string
  '--changelog-secondary-text': string
  '--changelog-tertiary': string
  '--changelog-tertiary-text': string

  // Feature type colors
  '--changelog-feature-bg': string
  '--changelog-feature-border': string
  '--changelog-feature-text': string
  '--changelog-feature-badge': string

  '--changelog-improvement-bg': string
  '--changelog-improvement-border': string
  '--changelog-improvement-text': string
  '--changelog-improvement-badge': string

  '--changelog-fix-bg': string
  '--changelog-fix-border': string
  '--changelog-fix-text': string
  '--changelog-fix-badge': string

  // Interactive elements
  '--changelog-link': string
  '--changelog-link-hover': string
  '--changelog-input-bg': string
  '--changelog-input-border': string
  '--changelog-input-focus': string
  '--changelog-checkbox-checked': string

  // Effects
  '--changelog-shadow': string

  // Admin-specific colors
  '--changelog-admin-bg': string
  '--changelog-admin-border': string
  '--changelog-admin-icon': string
  '--changelog-admin-badge': string
  '--changelog-admin-text': string
}

// Light theme aligned with El Toro's light theme
export const lightChangelogTheme: ChangelogTheme = {
  // Base colors from El Toro light theme
  '--changelog-bg': 'hsl(0, 0%, 100%)', // --color-white
  '--changelog-text': 'hsl(0, 0%, 8%)', // --color-grey-900
  '--changelog-text-subdued': 'hsl(0, 0%, 45%)', // --color-grey-600
  '--changelog-border': 'hsl(0, 0%, 84%)', // --color-grey-200
  '--changelog-filter-bg': 'hsl(0, 0%, 97%)', // --color-grey-050

  // El Toro brand colors from light theme
  '--changelog-primary': 'hsl(48, 23%, 60%)', // --color-primary-400
  '--changelog-primary-text': 'hsl(48, 23%, 20%)', // --color-primary-800
  '--changelog-secondary': 'hsl(210, 63%, 47%)', // --color-secondary-500
  '--changelog-secondary-text': 'hsl(210, 63%, 15%)', // --color-secondary-800
  '--changelog-tertiary': 'hsl(358, 71%, 40%)', // --color-tertiary-500
  '--changelog-tertiary-text': 'hsl(358, 71%, 13%)', // --color-tertiary-900

  // Feature type colors - light theme
  '--changelog-feature-bg': 'hsl(147, 50%, 95%)',
  '--changelog-feature-border': 'hsl(147, 50%, 50%)',
  '--changelog-feature-text': 'hsl(147, 50%, 25%)',
  '--changelog-feature-badge': 'hsl(147, 50%, 85%)',

  '--changelog-improvement-bg': 'hsl(210, 50%, 95%)',
  '--changelog-improvement-border': 'hsl(210, 50%, 50%)',
  '--changelog-improvement-text': 'hsl(210, 50%, 25%)',
  '--changelog-improvement-badge': 'hsl(210, 50%, 85%)',

  '--changelog-fix-bg': 'hsl(358, 50%, 95%)',
  '--changelog-fix-border': 'hsl(358, 50%, 50%)',
  '--changelog-fix-text': 'hsl(358, 50%, 25%)',
  '--changelog-fix-badge': 'hsl(358, 50%, 85%)',

  // Interactive elements - light theme
  '--changelog-link': 'hsl(210, 63%, 47%)', // --color-secondary
  '--changelog-link-hover': 'hsl(210, 63%, 35%)', // --color-secondary-600
  '--changelog-input-bg': 'hsl(0, 0%, 100%)', // --color-white
  '--changelog-input-border': 'hsl(0, 0%, 74%)', // --color-grey-300
  '--changelog-input-focus': 'hsl(210, 63%, 47%)', // --color-secondary
  '--changelog-checkbox-checked': 'hsl(210, 63%, 47%)', // --color-secondary

  // Effects
  '--changelog-shadow': '0 1px 3px rgba(0, 0, 0, 0.1)',

  // Admin-specific colors
  '--changelog-admin-bg': 'hsl(48, 25%, 98%)',
  '--changelog-admin-border': 'hsl(48, 35%, 75%)',
  '--changelog-admin-icon': 'hsl(48, 50%, 45%)',
  '--changelog-admin-badge': 'hsl(48, 25%, 90%)',
  '--changelog-admin-text': 'hsl(48, 30%, 25%)',
}

// Dark theme aligned with El Toro's dark theme
export const darkChangelogTheme: ChangelogTheme = {
  // Base colors from El Toro dark theme
  '--changelog-bg': 'hsla(0, 6%, 15%, 1)', // --base-bg
  '--changelog-text': 'hsla(48, 23%, 90%, 0.9)', // --base-text
  '--changelog-text-subdued': 'hsl(20, 1%, 70%)', // adjusted for dark
  '--changelog-border': 'hsl(20, 1%, 30%)', // adjusted for dark
  '--changelog-filter-bg': 'hsl(20, 1%, 22%)', // adjusted for dark

  // El Toro brand colors from dark theme
  '--changelog-primary': 'hsl(48, 23%, 59%)', // --tint-khaki
  '--changelog-primary-text': 'hsl(48, 6%, 95%)', // adjusted for dark
  '--changelog-secondary': 'hsl(210, 63%, 60%)', // adjusted for dark
  '--changelog-secondary-text': 'hsl(209, 8%, 90%)', // adjusted for dark
  '--changelog-tertiary': 'hsl(358, 71%, 50%)', // adjusted for dark
  '--changelog-tertiary-text': 'hsl(358, 22%, 90%)', // adjusted for dark

  // Feature type colors - dark theme
  '--changelog-feature-bg': 'hsl(147, 35%, 20%)',
  '--changelog-feature-border': 'hsl(147, 50%, 40%)',
  '--changelog-feature-text': 'hsl(147, 50%, 75%)',
  '--changelog-feature-badge': 'hsl(147, 35%, 30%)',

  '--changelog-improvement-bg': 'hsl(210, 35%, 25%)',
  '--changelog-improvement-border': 'hsl(210, 50%, 45%)',
  '--changelog-improvement-text': 'hsl(210, 50%, 75%)',
  '--changelog-improvement-badge': 'hsl(210, 35%, 35%)',

  '--changelog-fix-bg': 'hsl(358, 35%, 25%)',
  '--changelog-fix-border': 'hsl(358, 50%, 45%)',
  '--changelog-fix-text': 'hsl(358, 50%, 75%)',
  '--changelog-fix-badge': 'hsl(358, 35%, 35%)',

  // Interactive elements - dark theme
  '--changelog-link': 'hsl(210, 63%, 60%)', // adjusted for dark
  '--changelog-link-hover': 'hsl(210, 63%, 70%)', // adjusted for dark
  '--changelog-input-bg': 'hsl(20, 1%, 25%)', // adjusted for dark
  '--changelog-input-border': 'hsl(20, 1%, 43%)', // adjusted for dark
  '--changelog-input-focus': 'hsl(210, 63%, 60%)', // adjusted for dark
  '--changelog-checkbox-checked': 'hsl(210, 63%, 60%)', // adjusted for dark

  // Effects
  '--changelog-shadow': '0 1px 3px rgba(0, 0, 0, 0.3)',

  // Admin-specific colors
  '--changelog-admin-bg': 'hsl(48, 20%, 22%)',
  '--changelog-admin-border': 'hsl(48, 25%, 40%)',
  '--changelog-admin-icon': 'hsl(48, 40%, 70%)',
  '--changelog-admin-badge': 'hsl(48, 20%, 30%)',
  '--changelog-admin-text': 'hsl(48, 30%, 85%)',
}

export const themeMap = {
  light: lightChangelogTheme,
  dark: darkChangelogTheme,
} as const
